import { useMemo, useState } from "react";
import { Button, Description, Label, SearchField } from "@heroui/react";
import { Icon } from "../Icon";
import { PanelToggle } from "../NewsLayout";
import { PanelStatus } from "../PanelStatus";
import { SidebarSection } from "./SidebarSection";
import { SourceList, type FavoriteChooser, type Selection } from "./SourceList";
import type { Source, SubSource } from "../../data/mock";

const matches = (item: Source | SubSource, query: string): boolean =>
  item.name.toLowerCase().includes(query) || (item.children ?? []).some((c) => matches(c, query));

/** Le selezioni rapide del pannello delle fonti. */
export type BulkMode = "all" | "none" | "favorites" | "others";

const BULK_ACTIONS: { mode: BulkMode; label: string }[] = [
  { mode: "all", label: "Seleziona tutte" },
  { mode: "none", label: "Deseleziona tutte" },
  { mode: "favorites", label: "Seleziona i preferiti" },
  { mode: "others", label: "Seleziona gli altri" },
];

export const SOURCES_TITLE = "Gestione delle fonti";
export const DATE_FILTER_TITLE = "Filtro per data";

/** The title row of a side panel: the panel's icon, the title and the chevron that closes it (NewsLayout). */
export function PanelTitle({ title, onCollapse }: { title: string; onCollapse: () => void }) {
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2.5">
        <Icon name="sliders" />
        <h2 className="text-xl leading-7 font-semibold text-brand">{title}</h2>
      </div>
      <PanelToggle title={title} onPress={onCollapse} />
    </div>
  );
}

/** Lista con altezza massima di 6 righe e barra di scorrimento del design. */
export function ScrollArea({ children, maxHeight = 336 }: { children: React.ReactNode; maxHeight?: number }) {
  return (
    <div className="scroll-fonti -mr-3 overflow-y-auto pr-[7px]" style={{ maxHeight }}>
      {children}
    </div>
  );
}

// ---- UltimeNews: "Gestione delle fonti" ------------------------------------

/** Un avviso del pannello: il messaggio e, quando ha senso, come riprovare. */
export type PanelError = { message: string; onRetry?: () => void };

type FontiPanelProps = {
  onCollapse: () => void;
  /** Il catalogo: enti attivi, fonti eliminate, totale; null finché non è arrivato. */
  sources: Source[] | null;
  deleted: Source[];
  totalSources: number | null;
  status: "loading" | "error" | "ready";
  onRetry: () => void;
  /** Le spunte: il filtro delle liste (una sezione, o un ente/ramo con tutte le sezioni spuntate). */
  selection: Selection;
  onSelect: (item: Source | SubSource, checked: boolean) => void;
  /** La selezione rapida a cui il filtro corrisponde (aria-pressed); null se a nessuna. */
  bulkMode: BulkMode | null;
  /** Le selezioni rapide che hanno un effetto: «preferiti» e «altri» non ce l'hanno senza pin o con tutti i pin. */
  bulkAvailable: Record<BulkMode, boolean>;
  onBulk: (mode: BulkMode) => void;
  /** Il contenuto del popover del pin di un ente (la scelta delle cartelle). */
  favoriteChooser: FavoriteChooser;
  /** Destinazione del nome di un ente o di una sezione (la vista di quella fonte). */
  linkTo: (item: Source | SubSource) => string | null;
  /** L'ultima modifica dei preferiti rifiutata. */
  followError?: PanelError | null;
  /** L'ultimo salvataggio in una cartella rifiutato. */
  folderError?: PanelError | null;
};

export function FontiPanel({
  onCollapse,
  sources,
  deleted,
  totalSources,
  status,
  onRetry,
  selection,
  onSelect,
  bulkMode,
  bulkAvailable,
  onBulk,
  favoriteChooser,
  linkTo,
  followError,
  folderError,
}: FontiPanelProps) {
  const [query, setQuery] = useState("");

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const all = sources ?? [];
    return q ? all.filter((s) => matches(s, q)) : all;
  }, [sources, query]);

  const defaultExpanded = useMemo(() => sources?.filter((s) => s.name.toLowerCase() === "parlamento").map((s) => s.id) ?? [], [sources]);

  let list: React.ReactNode;
  if (status === "loading") list = <PanelStatus>Caricamento delle fonti…</PanelStatus>;
  else if (status === "error") list = <PanelStatus role="alert" onRetry={onRetry}>Fonti non disponibili.</PanelStatus>;
  else if (!sources || sources.length === 0) list = <PanelStatus>Nessuna fonte nel catalogo.</PanelStatus>;
  else if (visible.length === 0) list = <PanelStatus>Nessuna fonte corrisponde a «{query.trim()}».</PanelStatus>;
  else
    list = (
      <ScrollArea maxHeight={496}>
        <SourceList
          sources={visible}
          selection={selection}
          onSelect={onSelect}
          favoriteChooser={favoriteChooser}
          linkTo={linkTo}
          defaultExpandedKeys={defaultExpanded}
        />
      </ScrollArea>
    );

  return (
    <div className="flex flex-col gap-[30px]">
      <PanelTitle title={SOURCES_TITLE} onCollapse={onCollapse} />

      <SearchField value={query} onChange={setQuery} className="flex flex-col gap-1">
        <Label className="text-sm leading-6 font-medium text-accent">Cerca la fonte</Label>
        <SearchField.Group className="h-9 rounded-xl bg-field-background px-3 shadow-field">
          <SearchField.SearchIcon>
            <Icon name="magnifier" />
          </SearchField.SearchIcon>
          <SearchField.Input placeholder="Search..." className="text-sm leading-5" />
          <SearchField.ClearButton />
        </SearchField.Group>
        <Description className="text-xs leading-5 text-muted">
          {totalSources === null ? "Fonti totali: …" : `${totalSources} fonti totali`}
        </Description>
      </SearchField>

      <SidebarSection title="Fonti attive" icon="arrows3RotateRight" defaultExpanded>
        <div className="flex flex-wrap gap-2.5">
          {BULK_ACTIONS.map(({ mode, label }) => {
            const active = bulkMode === mode;
            return (
              <Button
                key={mode}
                size="sm"
                isDisabled={!bulkAvailable[mode] || !sources || sources.length === 0}
                onPress={() => onBulk(mode)}
                aria-pressed={active}
                className={`h-7 min-w-8 gap-1 rounded-2xl px-2.5 py-1 text-xs leading-5 font-medium ${
                  active ? "bg-accent text-default" : "bg-white text-field-placeholder shadow-field"
                }`}
              >
                <Icon name={active ? "circleCheck" : "circle"} />
                {label}
              </Button>
            );
          })}
        </div>
        {followError && (
          <PanelStatus role="alert" onRetry={followError.onRetry}>
            {followError.message}
          </PanelStatus>
        )}
        {folderError && (
          <PanelStatus role="alert" onRetry={folderError.onRetry}>
            {folderError.message}
          </PanelStatus>
        )}
        {list}
      </SidebarSection>

      <SidebarSection title="Fonti eliminate" icon="trashBin">
        <ul className="flex flex-col">
          {deleted.flatMap((s) =>
            s.children && s.children.length > 0
              ? s.children.map((c) => (
                  <li key={c.id} className="py-2 pl-7 text-sm leading-6 text-muted line-through">
                    {s.name} · {c.name}
                  </li>
                ))
              : [
                  <li key={s.id} className="py-2 pl-7 text-sm leading-6 text-muted line-through">
                    {s.name}
                  </li>,
                ],
          )}
          {status === "ready" && deleted.length === 0 && (
            <li className="py-2 pl-7 text-sm leading-6 text-muted">Nessuna fonte eliminata.</li>
          )}
        </ul>
      </SidebarSection>
    </div>
  );
}

// ---- NewsSalvate: "Filtro per data" ----------------------------------------

/** A week of a month in the date filter: its id (YYYY-MM-Www) and its days («5–11 ottobre»). */
export type DateFilterWeek = { id: string; name: string };

/** A year of the date filter: its months, each with its id (YYYY-MM), its name and, when given, its weeks. */
export type DateFilterYear = { year: string; months: { id: string; name: string; weeks?: DateFilterWeek[] }[] };

type DateFilterPanelProps = {
  /** Closes the panel: the chevron of the title (NewsLayout). */
  onCollapse: () => void;
  /** The years and their months, newest first; null until they have arrived. */
  years: DateFilterYear[] | null;
  status: "loading" | "error" | "ready";
  /** The ticked months and weeks, by id; a month ticked in part is "mixed". */
  selection: Selection;
  /** The months (ids) open on their weeks when the years first render. */
  defaultExpandedMonths?: string[];
  onSelect: (id: string, checked: boolean) => void;
  /** What stands under the months: the actions on the saved news («Pulisci tutto»). */
  actions?: React.ReactNode;
};

export function DateFilterPanel({
  onCollapse,
  years,
  status,
  selection,
  defaultExpandedMonths,
  onSelect,
  actions,
}: DateFilterPanelProps) {
  let list: React.ReactNode;
  if (status === "loading") list = <PanelStatus>Caricamento dei mesi…</PanelStatus>;
  // the list of news says it, with «Riprova»: here only a notice, not a second alert
  else if (status === "error") list = <PanelStatus role="note">Mesi non disponibili.</PanelStatus>;
  else if (!years || years.length === 0) list = <PanelStatus>Nessun mese con news salvate.</PanelStatus>;
  else
    list = years.map(({ year, months }, i) => {
      // a month with weeks opens on them, as an organisation opens on its sections
      const items: Source[] = months.map((m) => ({
        id: m.id,
        name: m.name,
        ...(m.weeks?.length ? { children: m.weeks.map((w) => ({ id: w.id, name: w.name })) } : {}),
      }));
      return (
        <SidebarSection key={year} title={year} icon="folderOpen" defaultExpanded={i === 0}>
          <ScrollArea>
            <SourceList
              sources={items}
              selection={selection}
              showIndicators={false}
              defaultExpandedKeys={defaultExpandedMonths}
              onSelect={(item, checked) => onSelect(item.id, checked)}
            />
          </ScrollArea>
        </SidebarSection>
      );
    });

  return (
    <div className="flex flex-col gap-[30px]">
      <PanelTitle title={DATE_FILTER_TITLE} onCollapse={onCollapse} />
      {list}
      {actions}
    </div>
  );
}
